import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserInfo, updateProfile } from '@/api';

/** 选中图片压缩为正方形 base64（头像用，避免原图过大） */
function compressToSquare(file: File, size = 240): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('canvas 不可用'));
          return;
        }
        //居中裁剪为正方形
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => reject(new Error('图片读取失败'));
      img.src = ev.target?.result as string;
    };
    reader.onerror = () => reject(new Error('图片读取失败'));
    reader.readAsDataURL(file);
  });
}

export default function EditProfile() {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(''); //已有头像 URL
  const [avatar, setAvatar] = useState(''); //新选的 base64，空表示未更换
  const [avatarError, setAvatarError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMsg(msg);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2000);
  }, []);

  //加载当前资料
  useEffect(() => {
    let mounted = true;
    getUserInfo()
      .then((res) => {
        if (!mounted) return;
        setNickname(res.user.nickname || '');
        setAvatarUrl(res.user.avatar || '');
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        showToast(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [showToast]);

  const handleAvatarChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; //允许重复选同一文件
    if (!file) return;
    try {
      const base64 = await compressToSquare(file);
      setAvatar(base64);
      setAvatarError('');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '图片处理失败');
    }
  }, [showToast]);

  //保存资料
  const handleSave = useCallback(async () => {
    if (!nickname.trim()) {
      showToast('请输入昵称');
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await updateProfile(nickname.trim(), avatar);
      showToast('资料修改成功');
      setTimeout(() => navigate(-1), 1200);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, [nickname, avatar, saving, showToast, navigate]);

  return (
    <div className="mobile-container bg-white min-h-screen">
      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[100] bg-foreground-900/90 text-white text-sm px-4 py-2.5 rounded-full shadow-lg">
          {toastMsg}
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-background-200/60">
        <div className="flex items-center px-3 h-12">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="返回"
          >
            <i className="ri-arrow-left-s-line text-xl text-foreground-700" />
          </button>
          <h1 className="flex-1 text-center text-base font-medium text-foreground-900 pr-8">
            编辑资料
          </h1>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin" />
        </div>
      ) : (
        <main className="px-4 py-6">
          {/* Avatar */}
          <div className="flex flex-col items-center mb-8">
            <div
              className="relative cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-20 h-20 rounded-full bg-background-100 border-2 border-background-200 flex items-center justify-center overflow-hidden">
                {avatar || avatarUrl ? (
                  <img src={avatar || avatarUrl} alt="头像" className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-user-3-line text-foreground-300 text-3xl" />
                )}
              </div>
              {/* 换图角标 */}
              <div className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-primary-500 border-2 border-white flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                <i className="ri-camera-line text-white text-xs" />
              </div>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-2.5 text-primary-500 text-xs cursor-pointer hover:text-primary-600 transition-colors"
            >
              点击更换头像
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />
            {avatarError && (
              <p className="text-red-500 text-xs mt-1.5">{avatarError}</p>
            )}
          </div>

          {/* Nickname */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-foreground-700 mb-2">昵称</label>
            <input
              type="text"
              maxLength={20}
              placeholder="请输入昵称"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 bg-white outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all"
            />
            <p className="text-foreground-300 text-xs mt-1.5 text-right">{nickname.length}/20</p>
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3.5 rounded-xl bg-primary-500 text-white text-base font-medium cursor-pointer hover:bg-primary-600 transition-colors active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg" />
                保存中...
              </>
            ) : (
              <>
                <i className="ri-check-line text-lg" />
                保存
              </>
            )}
          </button>
        </main>
      )}
    </div>
  );
}
